import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';

const ECOSYSTEM_PARTNER_CATEGORIES = [
  { id: 'accelerators', label: 'Accelerators' },
  { id: 'corporate-backed-accelerators', label: 'Corporate-Backed Accelerators' },
  { id: 'incubators', label: 'Incubators' },
  { id: 'technology-business-incubators', label: 'Technology Business Incubators' },
];

// Value icons: from Outline & Solid Red SVG Icons (non-(2) versions)
const VALUES = [
  {
    id: 'assertive',
    title: 'Assertive',
    description:
      "We take decisive action and provide strong guidance to help our portfolio companies achieve their ambitious goals.",
    icon: '/images/values/assertive.svg',
    bgColor: '#C9EAF2', // light blue (Water from design guide)
  },
  {
    id: 'brave',
    title: 'Brave',
    description:
      "We embrace bold ideas and support founders who dare to challenge conventions and take calculated risks.",
    icon: '/images/values/brave.svg',
    bgColor: '#C8E6C9', // light moss green
  },
  {
    id: 'innovative',
    title: 'Innovative',
    description:
      'We seek out disruptive technologies and novel business models that have the potential to transform industries.',
    icon: '/images/values/innovative.svg',
    bgColor: '#FFF9C4', // light yellow/amber
  },
  {
    id: 'dependable',
    title: 'Dependable',
    description:
      "Our founders can count on us through every stage of their journey. We're committed partners who deliver on our promises.",
    icon: '/images/values/dependable.png',
    bgColor: '#E1BEE7', // light purple/lavender
  },
  {
    id: 'ethical',
    title: 'Ethical',
    description:
      'We conduct business with the highest standards of integrity, transparency, and respect for all stakeholders.',
    icon: '/images/values/ethical.svg',
    bgColor: '#FCE4EC', // light pink
  },
];

const ValueCard = ({ value, index }) => (
  <motion.div
    // Page-load entrance without ever looking "empty" on scroll
    initial={{ opacity: 1, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.08 }}
    whileHover={{
      y: -6,
      scale: 1.02,
      transition: { type: 'spring', stiffness: 320, damping: 22 },
    }}
    whileTap={{ scale: 0.99 }}
    className="group flex flex-col rounded-2xl overflow-hidden border border-gray-100 shadow-sm flex-shrink-0 w-full transition-shadow hover:shadow-md"
    style={{ backgroundColor: value.bgColor }}
  >
    <div className="p-6 md:p-8 flex flex-col h-full">
      <div
        className="w-20 h-20 md:w-24 md:h-24 rounded-2xl flex items-center justify-center mb-5 flex-shrink-0 shadow-sm transition-transform duration-300 group-hover:scale-[1.04] group-hover:-rotate-1"
        style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
      >
        <img
          src={value.icon}
          alt=""
          aria-hidden="true"
          className="w-14 h-14 md:w-16 md:h-16 object-contain"
        />
      </div>
      <h3 className="text-xl font-bold text-[#211F1F] mb-3">{value.title}</h3>
      <p className="text-gray-700 leading-relaxed text-base flex-grow">
        {value.description}
      </p>
    </div>
  </motion.div>
);

const AboutPage = () => {
  usePageTitle('About');
  const location = useLocation();
  const logoGalleryRef = useRef(null);
  const [ecosystemManifest, setEcosystemManifest] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(ECOSYSTEM_PARTNER_CATEGORIES[0]?.id);

  useEffect(() => {
    if (!location.hash) return;
    const el = document.querySelector(location.hash);
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash]);

  useEffect(() => {
    let cancelled = false;
    fetch('/images/ecosystem-partners/manifest.json?v=20260224')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setEcosystemManifest(data);
        const first = data?.categories?.[0]?.id;
        if (first && !activeCategoryId) setActiveCategoryId(first);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categories = useMemo(() => {
    const loaded = ecosystemManifest?.categories;
    if (Array.isArray(loaded) && loaded.length > 0) return loaded;
    return ECOSYSTEM_PARTNER_CATEGORIES;
  }, [ecosystemManifest]);

  const activeLogos = useMemo(() => {
    const list = ecosystemManifest?.items?.[activeCategoryId];
    return Array.isArray(list) ? list : [];
  }, [ecosystemManifest, activeCategoryId]);

  // Preload all ecosystem partner images from every category to avoid lag on category switch
  useEffect(() => {
    if (!ecosystemManifest?.items) return;
    const allSrcs = Object.values(ecosystemManifest.items).flat();
    const seen = new Set();
    allSrcs.forEach((item) => {
      const url = encodeURI(item.src);
      if (seen.has(url)) return;
      seen.add(url);
      const img = new Image();
      img.src = url;
    });
  }, [ecosystemManifest]);

  // Infinite scroll: duplicate list and wrap scroll position
  const infiniteLogos = useMemo(() => (activeLogos.length > 0 ? [...activeLogos, ...activeLogos] : []), [activeLogos]);
  const onGalleryScroll = useRef(() => {});
  useEffect(() => {
    onGalleryScroll.current = () => {
      const el = logoGalleryRef.current;
      if (!el || activeLogos.length === 0) return;
      const half = el.scrollWidth / 2;
      if (el.scrollLeft >= half - 1) el.scrollLeft -= half;
    };
  }, [activeLogos.length]);

  useEffect(() => {
    const el = logoGalleryRef.current;
    if (!el) return;
    const handler = () => onGalleryScroll.current();
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [activeCategoryId]);

  const scrollGallery = (direction) => {
    const el = logoGalleryRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    const half = el.scrollWidth / 2;
    if (direction === 'left' && el.scrollLeft <= 0 && half > 0) {
      el.scrollLeft = half - amount;
      return;
    }
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const handleCategoryChange = (nextId) => {
    setActiveCategoryId(nextId);
    const el = logoGalleryRef.current;
    if (el) el.scrollTo({ left: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] relative">
      {/* Subtle background pattern */}
      <div
        className="absolute top-0 right-0 w-full md:w-2/5 h-96 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 70% 20%, #B60017 1px, transparent 1px), radial-gradient(circle at 30% 80%, #B60017 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
        }}
      />
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 pb-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>

            <h1 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-6">
              About <span className="text-[#C8473A]">Us</span>
            </h1>
          </motion.div>

          {/* Our Investment Philosophy */}
          <motion.section
            id="investment-philosophy"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-14 scroll-mt-36"
            aria-label="Our Investment Philosophy"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#211F1F] mb-8">
              Our{' '}
              <span className="text-[#C8473A]">Investment Philosophy</span>
            </h2>
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                We believe the best companies are built by &apos;Challengineer&apos; founders who have the courage to question the status quo, and the resilience to persevere through adversity.
              </p>
              <p>
                Our <span className="font-bold text-[#C8473A]">vision</span> is to nurture the success of every stakeholder associated with us.
              </p>
              <p>
                Our team&apos;s <span className="font-bold text-[#C8473A]">mission</span> is to make YourNest India&apos;s most envied VC by investing in ethical founders of innovative early-stage startups. In doing so, we will lead the industry, catalyse the growth of the economy, and multiply our investors&apos; wealth.
              </p>
              <p>
                Over the years, we have specialised in DeepTech and tech-enabled startups across sectors such as AI, robotics, Industry 4.0, quantum computing, SpaceTech, etc. And we remain open to investing in – and nurturing – enterprise-oriented, IP-led ventures.
              </p>
            </div>
          </motion.section>

          {/* Our Values */}
          <motion.section
            id="our-values"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mb-14 scroll-mt-36"
            aria-label="Our Values"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#211F1F] mb-8">
              Our <span className="text-[#C8473A]">Values</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {VALUES.map((value, index) => (
                <ValueCard key={value.id} value={value} index={index} />
              ))}
            </div>
          </motion.section>

          {/* Ecosystem Partners */}
          <motion.section
            id="ecosystem-partners"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-14 scroll-mt-36"
            aria-label="Ecosystem Partners"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#211F1F] mb-6">
              <span className="text-[#C8473A]">Ecosystem</span> Partners
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              As India&apos;s leading DeepTech VC, YourNest maintains active partnerships with the country&apos;s incubators, accelerators, industry associations and policymakers in the government. Our General Partners take part in all key dialogues to help grow India&apos;s venture capital industry.
            </p>
            {/* Scroll-snap logo gallery (with category filter) */}
            <div className="w-full" aria-label="Ecosystem partner logos">
              <motion.div
                ref={logoGalleryRef}
                tabIndex={0}
                role="region"
                aria-label="Ecosystem partner logo carousel"
                className="flex gap-8 md:gap-12 overflow-x-auto overflow-y-hidden py-6 px-2 md:px-4 scroll-smooth snap-x snap-mandatory focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8473A] focus-visible:ring-offset-2 rounded-lg"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {(infiniteLogos.length > 0 ? infiniteLogos : []).map((logo, idx) => (
                  <div
                    key={`${logo.src}-${idx}`}
                    className="flex-shrink-0 w-[140px] md:w-[160px] snap-center flex items-center justify-center"
                  >
                    <div className="w-full aspect-square max-h-[140px] md:max-h-[160px] bg-white rounded-xl flex items-center justify-center p-4 shadow-sm border border-gray-100">
                      <img
                        src={encodeURI(logo.src)}
                        alt={logo.alt || 'Ecosystem partner logo'}
                        className="w-full h-full object-contain"
                        loading="eager"
                      />
                    </div>
                  </div>
                ))}
                {infiniteLogos.length === 0 && (
                  <div className="text-sm text-gray-500 py-6 px-2">
                    No ecosystem partners found for this category.
                  </div>
                )}
              </motion.div>

              <div className="flex flex-wrap justify-center gap-3 mt-4">
                {categories.map((cat) => {
                  const isActive = cat.id === activeCategoryId;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => handleCategoryChange(cat.id)}
                      aria-pressed={isActive}
                      aria-label={`Show ${cat.label} ecosystem partners`}
                      className={[
                        'px-4 py-2 rounded-full text-sm font-medium border transition-colors',
                        isActive
                          ? 'bg-[#C8473A] border-[#C8473A] text-white'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-[#C8473A] hover:text-[#C8473A]',
                      ].join(' ')}
                    >
                      {cat.label}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => scrollGallery('left')}
                  className="p-2 rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-[#C8473A] hover:text-[#C8473A] transition-colors shadow-sm"
                  aria-label="Scroll ecosystem partner logos left"
                >
                  <ChevronLeft className="w-6 h-6" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollGallery('right')}
                  className="p-2 rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-[#C8473A] hover:text-[#C8473A] transition-colors shadow-sm"
                  aria-label="Scroll ecosystem partner logos right"
                >
                  <ChevronRight className="w-6 h-6" aria-hidden="true" />
                </button>
              </div>
            </div>
            <style>{`.overflow-x-auto::-webkit-scrollbar { display: none; }`}</style>
          </motion.section>

          {/* Fast-track Funding Programs */}
          <motion.section
            id="fast-track-funding"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mb-14 scroll-mt-36"
            aria-label="Fast-track Funding Programs"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#211F1F] mb-8">
              <span className="text-[#C8473A]">Fast-track Funding</span> Programs
            </h2>

            <div className="space-y-12">
              {/* Velocity 2024: title above, then text body left + logo right (logo aligned with body) */}
              <div>
                <h3 className="text-xl font-bold text-[#C8473A] mb-4">Velocity 2024</h3>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                  <div className="lg:col-span-7">
                    <p className="text-gray-700 leading-relaxed mb-4">
                      The Velocity fast-track funding program was a fortnight-long online event run in association with Sanchi Connect in May-June 2024. Focused on DeepTech startups, the Velocity team analysed 1,282 pitches in just 15 days, resulting in 79 being shortlisted for further interactions. Eventually, eight companies secured investments totalling US$ 5.78mn from Fund III.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      This cohort comprising <Link to="/portfolio?open=CargoFL" className="text-[#C8473A] font-medium hover:underline">CargoFL</Link>, <Link to="/portfolio?open=Induz" className="text-[#C8473A] font-medium hover:underline">Induz</Link>, <Link to="/portfolio?open=Leanworx" className="text-[#C8473A] font-medium hover:underline">Leanworx</Link>, <Link to="/portfolio?open=MapMyCrop" className="text-[#C8473A] font-medium hover:underline">MapMyCrop</Link>, <Link to="/portfolio?open=Perkant" className="text-[#C8473A] font-medium hover:underline">Perkant</Link>, <Link to="/portfolio?open=Presage" className="text-[#C8473A] font-medium hover:underline">Presage</Link>, <Link to="/portfolio?open=Superfone" className="text-[#C8473A] font-medium hover:underline">Superfone</Link> and <Link to="/portfolio?open=ThinkMetal" className="text-[#C8473A] font-medium hover:underline">ThinkMetal</Link> were inducted into our Nurture Capital thesis at a founders&apos; camp at the IIT Kanpur Outreach Centre in Noida, Delhi NCR, in December 2024.
                    </p>
                  </div>
                  <div className="lg:col-span-5 flex items-start justify-center lg:justify-end">
                    <div className="w-full max-w-[320px] bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-2px_rgba(0,0,0,0.08)] overflow-hidden p-6 flex items-center justify-center aspect-[4/3]">
                      <img
                        src="/images/velocity-logo.png?v=20260224"
                        alt="Velocity - Fast-track startup funding"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* SOAR 2020: title above, then text body left + logo right (logo aligned with body) */}
              <div>
                <h3 className="text-xl font-bold text-[#C8473A] mb-4">SOAR 2020</h3>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                  <div className="lg:col-span-7">
                    <p className="text-gray-700 leading-relaxed mb-4">
                      The YourNest SOAR program, a two-week pioneering fast-track funding initiative, was launched on 1 May 2020 to invest in a select few startups, despite the COVID19 pandemic that brought the world in general – and the venture funding industry in particular – to a grinding halt. At YourNest we were clear that this was, in fact, the ideal time for passionate, future-oriented startups to be encouraged even more.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Of the total 1,933 applications received (48% of which were in AI/ML &amp; Enterprise SaaS domains) 64 were shortlisted for a detailed evaluation and five startups that were selected in this program are: <Link to="/portfolio?open=Argoid" className="text-[#C8473A] font-medium hover:underline">Argoid</Link>, <Link to="/portfolio?open=Xpedize" className="text-[#C8473A] font-medium hover:underline">Xpedize</Link>, <Link to="/portfolio?open=LightspeedAI" className="text-[#C8473A] font-medium hover:underline">LightspeedAI</Link>, <Link to="/portfolio?open=UptimeAI" className="text-[#C8473A] font-medium hover:underline">UptimeAI</Link> and <Link to="/portfolio?open=Mauna" className="text-[#C8473A] font-medium hover:underline">Mauna</Link>. The program has invested between US$ 250,000 and US$ 1 mn in funding in each of these startups.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      YourNest SOAR was supported by several eco-system partners – Amazon Web Services, Cisco, Intel Maker Lab, Freshworks and JioNext along with Agnii and Invest India – who have committed their network, resources and specialists to the selected startups.
                    </p>
                  </div>
                  <div className="lg:col-span-5 flex items-start justify-center lg:justify-end">
                    <div className="w-full max-w-[320px] bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-2px_rgba(0,0,0,0.08)] overflow-hidden p-6 flex items-center justify-center aspect-[4/3]">
                      <img
                        src="/images/soar-logo.png?v=20260224"
                        alt="YourNest SOAR - Fast-track funding program"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* ESG */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mt-16"
            aria-label="ESG"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#C8473A] mb-6">
              ESG
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
              <div className="lg:col-span-7">
                <p className="text-gray-700 leading-relaxed">
                  YourNest is committed to being a responsible investor, considering environmental, social and governance issues across all its investments by complying with the ESG policy, applicable laws of the land and being responsive to existing and emerging ESG concerns. We adhere to our responsibility to people and the Planet while setting the stage for long-term success. Through ESG integration in our investment approach, we will endeavour to maximise business and investment opportunities. ESG integration will enable us to grow and improve the companies in which we invest, for long-term value generation opportunities for the benefit of all stakeholders. At the same time, we believe that ESG integration will help mitigate any liability, credit, market or reputation risks emanating from such issues.
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  As of 2026 YourNest Venture Capital is a UN PRI signatory. The Principles for Responsible Investment (PRI) is a United Nations-backed global framework supported by many across the world. This deepens our ongoing commitment to embedding ESG considerations across every phase of our investment process.
                </p>
                <Link
                  to="/about/esg"
                  className="inline-flex items-center gap-2 mt-6 text-[#C8473A] font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8473A] focus-visible:ring-offset-2 rounded"
                >
                  Explore our ESG approach
                </Link>
              </div>
              <div className="lg:col-span-5 flex items-start justify-center lg:justify-end">
                <div className="w-full max-w-[320px] bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-2px_rgba(0,0,0,0.08)] overflow-hidden p-6 flex items-center justify-center aspect-[4/3]">
                  <img
                    src="/images/pri-signatory-logo.png?v=20260601"
                    alt="PRI (Principles for Responsible Investment) signatory logo"
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
